import * as React from "react";
import { Box, Text, Flex, useColorMode, Center, VStack, Image } from "@chakra-ui/react";
import { TVLData, getTVL } from "./utils/tvl";
import { useLocation, Link } from "react-router-dom";
import { getGetInfoData } from "./utils/getinfo";
import { getBitcoinPrice } from "./utils/price";
import logoDark from "./assets/illustrations/logo-dark.svg";
import logoLight from "./assets/illustrations/logo-light.svg";
import { useFetchNumContracts } from "./hooks /useFetchNumContracts";

const App: React.FC = () => {
  const [TVL, setTVL] = React.useState<TVLData | null>(null);
  const [numTotalContracts, setNumTotalContracts] = React.useState<number | null>(null);
  const [bitcoinPrice, setBitcoinPrice] = React.useState<number | null>(null);
  const { numContracts: oysterNumContracts } = useFetchNumContracts("oyster");
  const { numContracts: engineNumContracts } = useFetchNumContracts("engine");

  const { colorMode, toggleColorMode } = useColorMode();

  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");

    if (
      (mode === "dark" && colorMode === "light") ||
      (mode === "light" && colorMode === "dark") ||
      (mode === null && colorMode === "light")
    ) {
      toggleColorMode();
    }
  }, [location, colorMode, toggleColorMode]);

  React.useEffect(() => {
    const fetchTVL = async () => {
      try {
        const tvlData = await getTVL();
        setTVL(tvlData);
      } catch (e) {
        console.error(e);
      }
    };

    fetchTVL(); // Fetch immediately on component mount

    const intervalId = setInterval(fetchTVL, 20000); // Fetch every 20 seconds

    return () => {
      clearInterval(intervalId); // Clean up on component unmount
    };
  }, []);

  React.useEffect(() => {
    const fetchNumTotalContracts = async () => {
      try {
        const info = await getGetInfoData("https://mm.atomic.finance");
        setNumTotalContracts(info.numDlcAccepts);
      } catch (e) {
        console.error(e);
      }
    };

    fetchNumTotalContracts(); // Fetch immediately on component mount

    const intervalId = setInterval(fetchNumTotalContracts, 60000); // Fetch every minute

    return () => {
      clearInterval(intervalId); // Clean up on component unmount
    };
  }, []);

  React.useEffect(() => {
    const fetchBitcoinPrice = async () => {
      try {
        const price = await getBitcoinPrice();
        setBitcoinPrice(price);
      } catch (e) {
        console.error(e);
      }
    };

    fetchBitcoinPrice(); // Fetch immediately on component mount

    const intervalId = setInterval(fetchBitcoinPrice, 20000); // Fetch every 20 seconds

    return () => {
      clearInterval(intervalId); // Clean up on component unmount
    };
  }, []);

  return (
    <Flex flexDirection="column" height="100vh">
      <Flex flexDirection="row" justifyContent="space-between" alignItems="center" padding="10px">
        <Link to={{ pathname: "https://atomic.finance/" }} target="_blank">
          <Image
            src={colorMode === "dark" ? logoDark : logoLight}
            alt="Logo"
            width="200px"
            height="100px"
            style={{ marginTop: -50 }}
          />
        </Link>
        <Box textAlign="center" my={4}>
          <Text fontSize="4xl">Sound Finance Dashboard</Text>
          <Text fontSize="2xl" color="gray.500">
            Atomic Finance Stats
          </Text>
        </Box>
        <Box width="200px" /> {/* This is a spacer to keep the title centered */}
      </Flex>
      <Flex flexDirection="row" height="50vh">
        <Box flex="1" borderWidth="1px" borderRadius="lg" padding="6" boxShadow="lg">
          <Center height="100%">
            {TVL === null ? (
              <Text>Loading...</Text>
            ) : (
              <VStack spacing={4}>
                <Text fontSize="3xl">Total Value Locked</Text>
                <Text fontSize="6xl">{TVL.btc.toFixed(2)} BTC</Text>
                <Text fontSize="3xl" color="gray.400">
                  {TVL.usd.toLocaleString()} USD
                </Text>
              </VStack>
            )}
          </Center>
        </Box>
        <Box flex="1" borderWidth="1px" borderRadius="lg" padding="6" boxShadow="lg">
          <Center height="100%">
            {numTotalContracts === null ? (
              <Text>Loading...</Text>
            ) : (
              <VStack spacing={4}>
                <Text fontSize="3xl">DLCs Created All Time</Text>
                <Text fontSize="6xl">{numTotalContracts}</Text>
              </VStack>
            )}
          </Center>
        </Box>
      </Flex>
      <Flex flexDirection="row" height="50vh">
        <Box flex="1" borderWidth="1px" borderRadius="lg" padding="6" boxShadow="lg">
          <Center height="100%">
            {numTotalContracts === null ? (
              <Text>Loading...</Text>
            ) : (
              <VStack spacing={4}>
                <Text fontSize="3xl">Number of active users</Text>
                <Text fontSize="6xl">{(oysterNumContracts ?? 0) + (engineNumContracts ?? 0)}</Text>
              </VStack>
            )}
          </Center>
        </Box>
        <Box flex="1" borderWidth="1px" borderRadius="lg" padding="6" boxShadow="lg">
          <Center height="100%">
            {bitcoinPrice === null ? (
              <Text>Loading...</Text>
            ) : (
              <VStack spacing={4}>
                <Text fontSize="3xl">Average BTC Invested</Text>
                <Text fontSize="6xl">1.29 BTC</Text>
                <Text fontSize="3xl" color="gray.400">
                  {(bitcoinPrice * 1.29).toFixed(2)} USD
                </Text>
              </VStack>
            )}
          </Center>
        </Box>
      </Flex>
    </Flex>
  );
};

export default App;
